import React, {
  ComponentPropsWithoutRef,
  PropsWithChildren,
  createContext,
  ReactElement,
  useCallback,
  forwardRef,
  useContext,
  useEffect,
  useState,
} from 'react';
import { VariantProps, cva } from 'class-variance-authority';
import { Command as CommandPrimitive } from 'cmdk';
import { Slot } from '@radix-ui/react-slot';
import { useMemo } from 'react';

import {
  Cancel as RemoveIcon,
  CheckboxUnchecked,
  CheckboxChecked,
  ArrowDown,
  ArrowUp,
  Loading,
} from '../icons';
import { CommandEmpty, CommandItem, CommandList, Command } from '../command/command';
import { PopoverContent, PopoverTrigger, Popover } from '../popover/popover';
import { typographyVariants, Typography } from '../typography/typography';
import { badgeVariants, BadgeProps, Badge } from '../badge/badge';
import { Separator } from '../separator/separator';
import { Button } from '../button/button';
import { cn } from '../../utils';

export const multiSelectorVariants = cva(
  'overflow-visible bg-transparent flex flex-col group/state-error group/state-required',
  {
    variants: {
      hasError: {
        true: 'state-error',
      },
      required: {
        true: 'state-required',
      },
    },
  },
);

interface IMultiSelectorItem {
  value: string;
  label: React.ReactNode | string;
  variant?: BadgeProps['variant'];
}

export interface MultiSelectorProps
  extends React.ComponentPropsWithoutRef<typeof CommandPrimitive>,
    VariantProps<typeof multiSelectorVariants> {
  searchFilterFunction?: (value: string, search: string) => number;
  onValuesChange: (value: string[]) => void;
  setIsOpen?: (value: boolean) => void;
  filterAndSort?: boolean;
  isLoading?: boolean;
  hasError?: boolean;
  floating?: boolean;
  required?: boolean;
  disabled?: boolean;
  values: string[];
  isOpen?: boolean;
  loop?: boolean;
}

// [&>div] - selector is needed to target div inside CommandList(it cant contain other elements except command items because of input logic)
export const multiselectorListVariant = cva('transition-colors w-full [&>div]:flex', {
  variants: {
    variant: {
      default: '[&>div]:flex-col',
      wrapped: '[&>div]:flex-wrap [&>div]:gap-2 [&>div]:p-2',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

interface MultiSelectContextProps {
  setOpen: (value: ((prev: boolean) => boolean) | boolean) => void;
  getItem: (value: string) => IMultiSelectorItem | undefined;
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  onValueChange: (value: string[] | string) => void;
  items: Record<string, IMultiSelectorItem>;
  ref: React.RefObject<HTMLInputElement>;
  isLoading?: boolean;
  activeIndex: number;
  inputValue: string;
  disabled?: boolean;
  floating: boolean;
  value: string[];
  open: boolean;
}

const MultiSelectContext = createContext<MultiSelectContextProps | null>(null);

const flattenChildren = (node: unknown): ReactElement[] => {
  if (!React.isValidElement(node)) return [];

  const props = node.props as PropsWithChildren;
  if (!props.children) return [node];

  return props.children
    ? [node, ...React.Children.toArray(props.children).flatMap(flattenChildren)]
    : [node];
};

const useMultiSelect = () => {
  const context = useContext(MultiSelectContext);
  if (!context) {
    throw new Error('useMultiSelect must be used within MultiSelectProvider');
  }
  return context;
};

const useMultiSelectItem = ({ value, label }: IMultiSelectorItem) => {
  const { value: options, onValueChange, setInputValue } = useMultiSelect();

  const item = useMemo<IMultiSelectorItem>(() => ({ value, label }), [value, label]);

  const mousePreventDefault = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const isSelected = useMemo(() => options.includes(item.value), [item.value, options]);

  const onSelect = useCallback(() => {
    onValueChange(item.value);
    setInputValue('');
  }, [item.value, onValueChange, setInputValue]);

  return { isSelected, onSelect, mousePreventDefault };
};

const MultiSelector = ({
  onValuesChange: onValueChange,
  searchFilterFunction,
  filterAndSort = true,
  values: value = [],
  floating = false,
  setIsOpen,
  className,
  isLoading,
  hasError,
  children,
  required,
  disabled,
  isOpen,
  dir,
  ...props
}: MultiSelectorProps) => {
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Collect items to display in value(by label). Can be changed if there is any better idea
  const items = useMemo(
    () =>
      React.Children.toArray(children)
        .flatMap(flattenChildren)
        .filter((child): child is ReactElement<IMultiSelectorItem> => {
          return child.type === MultiSelectorItem || child.type === MultiSelectorBadgeItem;
        })
        .reduce(
          (acc, children) => ({ ...acc, [children.props.value]: children.props }),
          {} as Record<string, IMultiSelectorItem>,
        ),
    [children],
  );

  const getItem = useCallback(
    (value: string): IMultiSelectorItem | undefined => {
      return items[value];
    },
    [items],
  );

  const onValueChangeHandler = useCallback(
    (val: string[] | string) => {
      if (Array.isArray(val)) return onValueChange(val);
      if (value.includes(val)) return onValueChange(value.filter((item) => item !== val));
      onValueChange([...value, val]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value],
  );

  const defaultFilterFunction = useCallback(
    (value: string, search: string) =>
      value.toLowerCase().startsWith(search.toLowerCase()) ? 1 : -1,
    [],
  );

  return (
    <MultiSelectContext.Provider
      value={{
        ref: inputRef as React.RefObject<HTMLInputElement>,
        activeIndex,
        inputValue,
        isLoading,
        floating,
        disabled,
        items,
        value,
        open: isOpen ?? open,
        onValueChange: onValueChangeHandler,
        // @ts-expect-error just for test purpose
        setOpen: setIsOpen ?? setOpen,
        setActiveIndex,
        setInputValue,
        getItem,
      }}
    >
      <Command
        className={cn(multiSelectorVariants({ hasError, required }), className)}
        filter={searchFilterFunction ?? defaultFilterFunction}
        shouldFilter={filterAndSort && open}
        dir={dir}
        {...props}
      >
        <Popover
          onOpenChange={(val) => {
            setOpen(val);
            setIsOpen?.(val);
            if (!val) {
              setInputValue('');
            }
          }}
          open={isOpen ?? open}
        >
          {children}
        </Popover>
      </Command>
    </MultiSelectContext.Provider>
  );
};

const MultiSelectorSeparator = ({ className }: React.HTMLAttributes<HTMLDivElement>) => (
  <Separator className={className} />
);
MultiSelectorSeparator.displayName = 'MultiSelectorSeparator';

export const multiSelectorTrigger = cva(
  `flex items-center justify-between gap-2 px-3 py-2 relative
  outline outline-border-default bg-background
  ring-1 ring-border-default rounded-md bg-background
  hover:outline-border-hover
  data-[state=open]:ring-2 data-[state=open]:ring-border-sky data-[state=open]:outline-surface data-[state=open]:outline-1
  group-[.state-error]/state-error:outline-critical
  `,
  {
    variants: {
      hasShadow: { true: 'shadow-sm disabled:shadow-none' },
      size: {
        small: 'min-h-10',
        medium: 'min-h-12',
        large: 'min-h-14',
      },
      floating: {
        true: 'relative',
      },
    },
    compoundVariants: [
      {
        floating: true,
        size: 'medium',
        class: 'pt-5 pb-0',
      },
      { floating: true, size: 'large', class: 'pt-5 pb-0' },
      { floating: true, size: 'small', class: 'pt-3 pb-0' },
    ],
    defaultVariants: {
      hasShadow: false,
      floating: false,
      size: 'medium',
    },
  },
);

export interface MultiSelectorTriggerProps
  extends Omit<VariantProps<typeof multiSelectorTrigger>, 'floating'>,
    React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
}

const MultiSelectorDefaultTrigger = forwardRef<HTMLDivElement, MultiSelectorTriggerProps>(
  ({ className, children, hasShadow, size, ...props }, ref) => {
    const { activeIndex, setActiveIndex, setOpen, open, floating, isLoading, disabled } =
      useMultiSelect();

    return (
      <div
        className={cn(
          multiSelectorTrigger({ hasShadow, size, floating }),
          {
            'ring-1 focus-within:ring-surface': activeIndex === -1,
            'cursor-wait': isLoading,
            relative: open,
            'opacity-70 text-muted-foreground outline-border-disabled cursor-not-allowed bg-body outline-none text-content-disabled':
              disabled,
          },
          className,
        )}
        onClick={() => {
          if (!isLoading && !disabled) {
            setOpen((prev) => !prev);
            setActiveIndex(-1);
          }
        }}
        data-state={open ? 'open' : 'closed'}
        aria-disabled={disabled}
        tabIndex={-1}
        ref={ref}
        {...props}
      >
        <div className="flex items-center gap-1 w-full overflow-hidden">{children}</div>
        <div
          className={cn('flex items-center h-6', {
            '-mt-4': floating,
          })}
        >
          {isLoading ? (
            <Loading className="size-4 animate-spin opacity-50" />
          ) : open ? (
            <ArrowUp className="size-4 opacity-50" />
          ) : (
            <ArrowDown className="size-4 opacity-50" />
          )}
        </div>
      </div>
    );
  },
);

MultiSelectorDefaultTrigger.displayName = 'MultiSelectorDefaultTrigger';

const MultiSelectorTrigger = forwardRef<HTMLDivElement, MultiSelectorTriggerProps>(
  ({ className, children, asChild, ...props }, ref) => {
    const { open, isLoading, disabled, floating } = useMultiSelect();
    const { hasShadow, size, ...rest } = props;

    return (
      <PopoverTrigger asChild>
        {asChild ? (
          <Slot
            data-state={open ? 'open' : 'closed'}
            data-has-shadow={hasShadow}
            data-disabled={disabled}
            data-floating={floating}
            data-loading={isLoading}
            className={className}
            data-size={size}
            ref={ref}
            {...rest}
          >
            {children}
          </Slot>
        ) : (
          <MultiSelectorDefaultTrigger
            className={className}
            hasShadow={hasShadow}
            size={size}
            ref={ref}
            {...rest}
          >
            {children}
          </MultiSelectorDefaultTrigger>
        )}
      </PopoverTrigger>
    );
  },
);

MultiSelectorTrigger.displayName = 'MultiSelectorTrigger';

const MultiSelectorValue = ({
  placeholder,
  children,
}: PropsWithChildren<{ placeholder?: string }>) => {
  const { value, getItem, floating } = useMultiSelect();
  const getItemLabel = useCallback((value: string) => getItem(value)?.label ?? value, [getItem]);

  return (
    <>
      {(floating || !value.length) && (
        <Typography
          className={cn(
            `absolute text-left z-10 text-weak transition-transform duration-300 origin-left top-1/2 -translate-y-1/2 whitespace-nowrap group-[.state-required]/state-required:after:content-['*'] group-[.state-required]/state-required:after:text-critical`,
            {
              'top-4/12 scale-75': value.length > 0,
            },
          )}
          variant="labelMediumBook"
        >
          {placeholder}
        </Typography>
      )}

      <Typography variant="labelMediumBook" className="truncate">
        {children || value.map(getItemLabel).join(', ')}
      </Typography>
    </>
  );
};

MultiSelectorValue.displayName = 'MultiSelectorValue';

export interface MultiSelectorBadgeValueProps extends VariantProps<typeof badgeVariants> {
  removeable?: boolean;
  placeholder?: string;
}

const MultiSelectorBadgeValue = ({
  removeable = false,
  size = 'small',
  placeholder,
  variant,
}: MultiSelectorBadgeValueProps) => {
  const { value, onValueChange, getItem, floating } = useMultiSelect();
  const getItemLabel = useCallback((value: string) => getItem(value)?.label ?? value, [getItem]);
  const getItemVariant = useCallback(
    (value: string) => getItem(value)?.variant ?? 'default',
    [getItem],
  );

  const mousePreventDefault = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  return (
    <>
      {(floating || !value.length) && (
        <Typography
          className={cn(
            `absolute text-left z-10 text-weak transition-transform duration-300 origin-left top-1/2 -translate-y-1/2 whitespace-nowrap group-[.state-required]/state-required:after:content-['*'] group-[.state-required]/state-required:after:text-critical`,
            {
              'top-3.5 scale-75': value.length > 0,
            },
          )}
          variant="labelMediumBook"
        >
          {placeholder}
        </Typography>
      )}

      <div className="flex gap-1 flex-wrap py-1.5">
        {value.map((item) => (
          <Badge
            className="whitespace-nowrap cursor-default"
            variant={variant || getItemVariant(item)}
            clickable={removeable}
            size={size}
            key={item}
            outlined
          >
            {getItemLabel(item)}
            {removeable && (
              <button
                onClick={(e) => {
                  onValueChange(item);
                  e.stopPropagation();
                }}
                aria-roledescription="button to remove option"
                aria-label={`Remove ${item} option`}
                onMouseDown={mousePreventDefault}
                className="cursor-pointer"
                type="button"
              >
                <span className="sr-only">Remove {item} option</span>
                <RemoveIcon className="size-3" />
              </button>
            )}
          </Badge>
        ))}
      </div>
    </>
  );
};

MultiSelectorBadgeValue.displayName = 'MultiSelectorBadgeValue';

const MultiSelectorInput = forwardRef<
  React.ComponentRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className, ...props }, _) => {
  const {
    setActiveIndex,
    setInputValue,
    activeIndex,
    inputValue,
    isLoading,
    disabled,
    setOpen,
    ref,
  } = useMultiSelect();

  useEffect(() => {
    // If value is set from outside, set it to input value
    if (props.value !== undefined && inputValue !== props.value) setInputValue(props.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value]);

  return (
    <div className="flex">
      <CommandPrimitive.Input
        {...props}
        className={cn(
          typographyVariants({ variant: 'labelMediumBook' }),
          'ml-2 bg-transparent outline-none placeholder:text-weak flex-1',
          className,
          activeIndex !== -1 && 'caret-transparent',
        )}
        onValueChange={(data) => {
          if (activeIndex === -1) {
            props.onValueChange?.(data);
            setInputValue(data);
          }
        }}
        onClick={(e) => {
          setActiveIndex(-1);
          e.stopPropagation();
        }}
        onFocus={() => !isLoading && !disabled && setOpen(true)}
        disabled={isLoading || disabled}
        value={inputValue}
        tabIndex={0}
        ref={ref}
      />
    </div>
  );
});

MultiSelectorInput.displayName = 'MultiSelectorInput';

const MultiSelectorContent = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ children, className }, _ref) => {
    return (
      <PopoverContent
        className={cn(
          `[&_input]:m-3 [&_input]:w-full p-0 w-[var(--radix-popover-trigger-width)] max-h-[var(--radix-popper-available-height)] overflow-hidden flex flex-col`,
          className,
        )}
        role="listbox"
      >
        {children}
      </PopoverContent>
    );
  },
);

MultiSelectorContent.displayName = 'MultiSelectorContent';

interface MultiSelectorListProps
  extends React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>,
    VariantProps<typeof multiselectorListVariant> {
  emptyContent?: React.ReactNode;
  displayEmptyContent?: boolean;
}

const MultiSelectorList = forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  MultiSelectorListProps
>(({ className, children, variant, emptyContent, displayEmptyContent = true }, ref) => {
  return (
    <CommandList
      className={cn(
        'p-1 [&::-webkit-scrollbar-track]:!bg-transparent [&::-webkit-scrollbar-thumb]:!bg-black/20 [&::-webkit-scrollbar-thumb]:!rounded flex-1',
        multiselectorListVariant({ variant }),
        className,
      )}
      ref={ref}
    >
      {children}
      {displayEmptyContent && (
        <CommandEmpty className="p-2">
          {emptyContent ? emptyContent : <span className="text-disabled">-</span>}
        </CommandEmpty>
      )}
    </CommandList>
  );
});

MultiSelectorList.displayName = 'MultiSelectorList';

// Checkbox component caused problems with forms(update loop)
const MultiSelectorItemDummyCheckbox = ({ checked }: { checked: boolean }) => {
  return checked ? (
    <CheckboxChecked className="size-4 text-surface" />
  ) : (
    <CheckboxUnchecked className="size-4 text-border-default" />
  );
};

const MultiSelectorItem = forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item> & {
    value: string[] | string;
    label: string;
  }
>(({ className, value, label, ...props }, ref) => {
  const { isSelected, onSelect, mousePreventDefault } = useMultiSelectItem({ value, label });

  return (
    <CommandItem
      ref={ref}
      {...props}
      className={cn(
        'rounded-md cursor-pointer px-3 py-3.5 transition-colors flex gap-2 items-center',
        typographyVariants({ variant: 'labelMediumBook' }),
        className,
        props.disabled && 'opacity-50 cursor-not-allowed',
      )}
      onMouseDown={mousePreventDefault}
      onSelect={onSelect}
    >
      <MultiSelectorItemDummyCheckbox checked={isSelected} />
      <span>{label}</span>
    </CommandItem>
  );
});

MultiSelectorItem.displayName = 'MultiSelectorItem';

const MultiSelectorBadgeItem = forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item> & {
    size?: BadgeProps['size'];
  } & IMultiSelectorItem
>(({ className, value, label, variant, size = 'large', ...props }, ref) => {
  const { isSelected, onSelect, mousePreventDefault } = useMultiSelectItem({ value, label });
  const { isLoading, disabled } = useMultiSelect();

  return (
    <CommandItem
      ref={ref}
      {...props}
      className={cn(
        'rounded-lg p-0 cursor-pointer transition-colors flex gap-2 items-center bg-transparent rounded-full',
        className,
        { 'opacity-50': isLoading || props.disabled || disabled },
        { 'cursor-not-allowed': props.disabled || isLoading || disabled },
      )}
      onSelect={isLoading || disabled ? undefined : props.onSelect || onSelect}
      onMouseDown={mousePreventDefault}
    >
      <Badge
        clickable={!isLoading && !disabled}
        variant={variant || 'default'}
        outlined={isSelected}
        size={size}
      >
        {label}
      </Badge>
    </CommandItem>
  );
});

MultiSelectorBadgeItem.displayName = 'MultiSelectorBadgeItem';

interface MultiSelectorSelectAllItemProps extends React.HTMLAttributes<HTMLDivElement> {
  label: string;
}

const MultiSelectorSelectAllItem = (props: MultiSelectorSelectAllItemProps) => {
  const { className, label, ...rest } = props;
  const { value, onValueChange, items } = useMultiSelect();

  const allItemsSelected = value.length === Object.keys(items).length;

  return (
    <div className={cn('border-b border-border-weak px-1 py-1', className)} {...rest}>
      <CommandItem
        className={cn('flex items-center cursor-pointer px-3 py-3.5 gap-2', className)}
        onSelect={() => onValueChange(allItemsSelected ? [] : Object.keys(items))}
      >
        <MultiSelectorItemDummyCheckbox checked={allItemsSelected} />
        <Typography variant="labelMediumBook">{label}</Typography>
      </CommandItem>
    </div>
  );
};

const MultiSelectorContentClose = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<typeof Button>
>(({ children, asChild, onClick, ...props }, ref) => {
  const { setOpen } = useMultiSelect();
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      onClick?.(e);
      setOpen(false);
    },
    [onClick, setOpen],
  );
  return asChild ? (
    <Slot onClick={handleClick} ref={ref} {...props} />
  ) : (
    <Button onClick={handleClick} ref={ref} {...props}>
      {children}
    </Button>
  );
});

MultiSelectorContentClose.displayName = 'MultiSelectorContentClose';
export {
  MultiSelectorSelectAllItem,
  MultiSelectorContentClose,
  MultiSelectorBadgeValue,
  MultiSelectorBadgeItem,
  MultiSelectorSeparator,
  MultiSelectorTrigger,
  MultiSelectorContent,
  MultiSelectorInput,
  MultiSelectorValue,
  MultiSelectorList,
  MultiSelectorItem,
  MultiSelector,
};
